import { forwardRef, memo, useCallback, useMemo } from 'react';
import toSafeInteger from 'lodash/toSafeInteger';
import { useIntl } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
// Skillmore UI Components
import { injectParams } from '@empathco/ui-components/src/helpers/path';
import TargetIcon from '@empathco/ui-components/src/elements/TargetIcon';
import ItemCard from '@empathco/ui-components/src/elements/ItemCard';
import SkillLevelGauge from '@empathco/ui-components/src/elements/SkillLevelGauge';
import SkillTrendIcon from '@empathco/ui-components/src/elements/SkillTrendIcon';
import SkillNewTag from '@empathco/ui-components/src/elements/SkillNewTag';
// local imports
import { Skill, SKILL_LEVEL_MIN } from '../models/skill';
import { PATH_SKILL } from '../config/paths';
import { getSkillCurrentLevelOnly, isInferred } from '../helpers/models';
import CourseHours from '../elements/CourseHours';
import SuggestedSkillIcon from '../v3/SuggestedSkillIcon';
// SCSS imports
import { titleRow, nextIcon } from './SkillCard.module.scss';

type SkillCardProps = {
  readonly skill: Skill;
  readonly onLevelClick?: (skill: Skill) => void;
  readonly onRemoveClick?: (skill: Skill) => void;
  // onTargetClick?: (skill: Skill) => void;
  readonly withRemoveIcon?: boolean;
  readonly disabled?: boolean | null;
  readonly pendingTarget?: boolean;
  readonly pendingLevel?: boolean;
  readonly isEmployee?: boolean;
  readonly plain?: boolean;
  readonly depersonalised?: boolean;
  readonly withExpectedLevel?: boolean;
  readonly withCourses?: boolean;
  readonly withRemoveSuggested?: boolean;
  readonly fullWidth?: boolean;
  readonly narrowGrid?: boolean;
  readonly small?: boolean;
};

const SkillCard = forwardRef<HTMLDivElement, SkillCardProps>(({
  skill,
  onLevelClick,
  onRemoveClick,
  withRemoveIcon = false,
  disabled = false,
  pendingLevel = false,
  pendingTarget = false,
  isEmployee = false,
  plain = false,
  depersonalised = false,
  withExpectedLevel = false,
  withCourses = false,
  withRemoveSuggested = false,
  fullWidth = false,
  narrowGrid = false,
  small = false
  // onTargetClick
  // eslint-disable-next-line complexity
}, ref) => {
  const {
    abbr, title, expected_level, inferred_level, is_in_demand, is_new, is_target, is_inference_newer,
    course_hours, course_titles, is_suggested_by_manager
  } = skill || {};
  const currentLevel = depersonalised ? undefined : getSkillCurrentLevelOnly(skill);
  const { formatMessage } = useIntl();
  const removeAriaLabel = useMemo(
    () => onRemoveClick ? formatMessage({ id: 'common.button.remove_item' }, { name: title }) : undefined,
    [onRemoveClick, formatMessage, title]
  );

  const handleLevelClick = useCallback(() => {
    if (onLevelClick && skill && skill.id) onLevelClick(skill);
  }, [onLevelClick, skill]);

  const handleRemoveClick = useCallback(() => {
    if (onRemoveClick && skill && skill.id) onRemoveClick(skill);
  }, [onRemoveClick, skill]);

  // const handleTargetClick = useCallback(() => {
  //   if (onTargetClick && skill && skill.id) onTargetClick(skill);
  // }, [onTargetClick, skill]);

  const link = useMemo(() => injectParams(PATH_SKILL, { skill_id: abbr }), [abbr]);

  const icons = plain ? null : (
    <>
      {isEmployee && !depersonalised && (is_target || pendingTarget) ? (
        <TargetIcon
            small
            active
            tooltip={pendingTarget ? undefined : 'skill.target_this'}
            tooltipPlacement="top"
            // button
            // onClick={handleTargetClick}
            pending={pendingTarget}
            // disabled={disabled}
            // className={is_in_demand ? targetIcon : undefined}
            className={nextIcon}
        />
      ) : undefined}
      {is_suggested_by_manager && !depersonalised ? <SuggestedSkillIcon active className={nextIcon}/> : undefined}
      {/* {(!is_target || is_in_demand) && ( */}
      <SkillTrendIcon small active={is_in_demand}/>
      {/* )} */}
    </>
  );

  return (
    <ItemCard
        ref={ref}
        md={narrowGrid ? 6 : undefined}
        lg={narrowGrid ? 4 : undefined}
        xl={narrowGrid ? 3 : undefined}
        onClick={disabled || !onLevelClick ? undefined : handleLevelClick}
        onRemoveClick={!disabled && onRemoveClick && (
          (withRemoveIcon && currentLevel !== SKILL_LEVEL_MIN) ||
          (withRemoveSuggested ? is_suggested_by_manager : isInferred(skill))
        ) ? handleRemoveClick : undefined}
        removeAriaLabel={removeAriaLabel}
        link={disabled || onLevelClick ? undefined : link}
        badge
        fullWidth={fullWidth}
        footer={(
          <SkillLevelGauge
              level={currentLevel}
              depersonalised={depersonalised}
              requiredLevel={withExpectedLevel ? toSafeInteger(expected_level) : undefined}
              inferredLevel={inferred_level}
              isInferenceNewer={is_inference_newer}
              pending={pendingLevel}
          />
        )}
    >
      <Box
          display="flex"
          pr={onRemoveClick ? 1 : undefined}
          className={small ? undefined : titleRow}
      >
        {plain ? title : (
          <>
            <Box pr={1.5} flexGrow={1} flexBasis={0} className={small ? undefined : titleRow}>
              {isEmployee && !depersonalised && !small ? <SkillNewTag active={is_new}/> : undefined}
              <div>
                {title}
              </div>
            </Box>
            <Box
                display="flex"
                px={1}
                pt={!isEmployee || depersonalised || small ? 0.25 : '1.25rem'}
                flexDirection={withCourses ? 'column' : undefined}
            >
              {withCourses ? (
                <>
                  <Box display="flex" flexGrow={1} justifyContent="flex-end">
                    {icons}
                  </Box>
                  <CourseHours
                      hours={course_hours}
                      courses={course_titles}
                  />
                </>
              ) : icons}
            </Box>
          </>
        )}
      </Box>
    </ItemCard>
  );
});

SkillCard.displayName = 'SkillCard';

export default memo(SkillCard);
